import React, { useEffect, useState } from "react";
import SliderLearn from "../components/Homepage/SliderLearn";

export default function Homepage() {
  const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });
  const [glitchActive, setGlitchActive] = useState(false);

  useEffect(() => {
    const handleMouseMove = (e) => {
      setMousePosition({ x: e.clientX, y: e.clientY });
    };

    window.addEventListener('mousemove', handleMouseMove);
    
    // Trigger glitch effect randomly
    const glitchInterval = setInterval(() => {
      setGlitchActive(true);
      setTimeout(() => setGlitchActive(false), 500);
    }, 8000);

    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      clearInterval(glitchInterval);
    };
  }, []);

  return (
    <div className="min-h-screen bg-black overflow-hidden relative">
      {/* Cyberpunk Grid Background */}
      <div className="absolute inset-0 opacity-20">
        <div className="absolute inset-0" style={{
          backgroundImage: `
            linear-gradient(cyan 1px, transparent 1px),
            linear-gradient(90deg, cyan 1px, transparent 1px)
          `,
          backgroundSize: '50px 50px',
          animation: 'gridPulse 4s ease-in-out infinite'
        }}></div>
      </div>

      {/* Floating Neon Particles */}
      <div className="absolute inset-0 pointer-events-none">
        {[...Array(20)].map((_, i) => (
          <div
            key={i}
            className="absolute w-2 h-2 bg-cyan-400 rounded-full shadow-neon-cyan animate-float"
            style={{
              left: `${Math.random() * 100}%`,
              top: `${Math.random() * 100}%`,
              animationDelay: `${Math.random() * 5}s`,
              animationDuration: `${3 + Math.random() * 4}s`
            }}
          />
        ))}
        {[...Array(15)].map((_, i) => (
          <div
            key={`pink-${i}`}
            className="absolute w-1 h-1 bg-pink-500 rounded-full shadow-neon-pink animate-float"
            style={{
              left: `${Math.random() * 100}%`,
              top: `${Math.random() * 100}%`,
              animationDelay: `${Math.random() * 5}s`,
              animationDuration: `${2 + Math.random() * 3}s`
            }}
          />
        ))}
      </div>

      {/* Dynamic Mouse Cursor Effect */}
      <div 
        className="absolute w-64 h-64 pointer-events-none z-10 opacity-30 rounded-full"
        style={{
          left: mousePosition.x - 128,
          top: mousePosition.y - 128,
          background: 'radial-gradient(circle, rgba(0,255,255,0.3) 0%, rgba(255,0,255,0.2) 50%, transparent 70%)',
          transition: 'all 0.1s ease-out'
        }}
      />

      {/* Hero Section */}
      <section className="relative pt-24 pb-16 px-4 overflow-hidden">
        {/* Holographic Background Elements */}
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute -top-40 -right-40 w-80 h-80 bg-gradient-to-br from-cyan-400/30 to-blue-600/30 rounded-full blur-3xl animate-pulse-neon"></div>
          <div className="absolute -bottom-40 -left-40 w-80 h-80 bg-gradient-to-br from-pink-400/30 to-purple-600/30 rounded-full blur-3xl animate-pulse-neon delay-1000"></div>
          <div className="absolute top-1/2 left-1/2 w-96 h-96 bg-gradient-to-br from-green-400/20 to-emerald-600/20 rounded-full blur-3xl animate-spin-slow"></div>
        </div>

        {/* Scanlines Effect */}
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute inset-0 bg-gradient-to-b from-transparent via-cyan-500/5 to-transparent animate-scanlines"></div>
        </div>

        <div className="relative max-w-7xl mx-auto text-center z-20">
          {/* Glitch Main Heading */}
          <div className="mb-8">
            <h1 className={`text-6xl md:text-8xl font-black mb-6 animate-fadeIn ${glitchActive ? 'animate-glitch' : ''}`}>
              <span className="relative inline-block">
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-cyan-400 via-pink-500 to-purple-500 animate-gradient-x">
                  CYBER
                </span>
                <span className="absolute inset-0 text-transparent bg-clip-text bg-gradient-to-r from-pink-500 via-cyan-400 to-green-400 animate-gradient-x" style={{animationDelay: '0.1s'}}>
                  CYBER
                </span>
              </span>
              <span className="block text-5xl md:text-7xl text-white font-light tracking-widest">
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-green-400 via-cyan-500 to-blue-500 animate-gradient-x">
                  KNOWLEDGE
                </span>
              </span>
              <span className="block text-4xl md:text-6xl text-pink-400 font-bold tracking-wider animate-pulse-fast">
                HUB_
              </span>
            </h1>
            <p className="text-xl md:text-2xl text-gray-300 max-w-3xl mx-auto leading-relaxed animate-fadeIn delay-300 font-mono">
              [ INITIALIZING_NEURAL_NETWORK... ]<br/>
              <span className="text-cyan-400">{'>'}</span> Discover cutting-edge tech knowledge
              <span className="animate-blink text-green-400">█</span>
            </p>
          </div>

          {/* Holographic CTA Buttons */}
          <div className="flex flex-col sm:flex-row gap-6 justify-center mb-16 animate-fadeIn delay-500">
            <button className="group relative px-10 py-5 bg-transparent border-2 border-cyan-400 text-cyan-400 font-bold text-lg rounded-xl shadow-neon-cyan hover:shadow-neon-cyan-intense transform hover:scale-105 transition-all duration-300 overflow-hidden">
              <span className="relative z-10 font-mono tracking-wider">JACK_IN</span>
              <div className="absolute inset-0 bg-cyan-400 rounded-xl transform -skew-x-12 scale-x-0 group-hover:scale-x-100 transition-transform duration-500 opacity-20"></div>
              <div className="absolute inset-0 border border-cyan-400 rounded-xl animate-border-pulse"></div>
            </button>
            <button className="group relative px-10 py-5 bg-transparent border-2 border-pink-500 text-pink-500 font-bold text-lg rounded-xl shadow-neon-pink hover:shadow-neon-pink-intense transform hover:scale-105 transition-all duration-300 overflow-hidden">
              <span className="relative z-10 font-mono tracking-wider">EXPLORE_MATRIX</span>
              <div className="absolute inset-0 bg-pink-500 rounded-xl transform skew-x-12 scale-x-0 group-hover:scale-x-100 transition-transform duration-500 opacity-20"></div>
              <div className="absolute inset-0 border border-pink-500 rounded-xl animate-border-pulse"></div>
            </button>
          </div>

          {/* Cyberpunk Feature Grid */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-6xl mx-auto">
            {/* AI & Machine Learning Card */}
            <div className="group relative bg-black/60 backdrop-blur-sm border border-cyan-400/50 rounded-2xl p-8 shadow-neon-cyan hover:shadow-neon-cyan-intense transform hover:-translate-y-4 hover:rotate-1 transition-all duration-500 animate-slideUp delay-700 overflow-hidden">
              <div className="absolute inset-0 bg-gradient-to-br from-cyan-500/10 to-blue-600/10 rounded-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
              <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-transparent via-cyan-400 to-transparent animate-slide-right rounded-t-2xl"></div>
              <div className="relative z-10">
                <div className="w-20 h-20 bg-black border-2 border-cyan-400 rounded-2xl flex items-center justify-center mb-6 mx-auto group-hover:scale-110 group-hover:rotate-12 transition-transform duration-300 shadow-neon-cyan">
                  <svg className="w-10 h-10 text-cyan-400" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z" />
                  </svg>
                </div>
                <h3 className="text-2xl font-bold text-cyan-400 mb-4 font-mono tracking-wider">
                  AI NEURAL CORE
                </h3>
                <p className="text-gray-300 leading-relaxed mb-6 font-mono text-sm">
                  {'>'} Initialize deep learning protocols<br/>
                  {'>'} Quantum computing algorithms<br/>
                  {'>'} Neural network optimization
                </p>
                <div className="flex flex-wrap gap-2">
                  <span className="px-3 py-1 bg-cyan-400/20 border border-cyan-400 text-cyan-400 text-xs font-mono rounded-lg">TensorFlow</span>
                  <span className="px-3 py-1 bg-pink-500/20 border border-pink-500 text-pink-500 text-xs font-mono rounded-lg">PyTorch</span>
                  <span className="px-3 py-1 bg-green-400/20 border border-green-400 text-green-400 text-xs font-mono rounded-lg">MLOps</span>
                </div>
              </div>
            </div>

            {/* Development & Technology Card */}
            <div className="group relative bg-black/60 backdrop-blur-sm border border-green-400/50 rounded-2xl p-8 shadow-neon-green hover:shadow-neon-green-intense transform hover:-translate-y-4 hover:-rotate-1 transition-all duration-500 animate-slideUp delay-900 overflow-hidden">
              <div className="absolute inset-0 bg-gradient-to-br from-green-500/10 to-emerald-600/10 rounded-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
              <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-transparent via-green-400 to-transparent animate-slide-right delay-500 rounded-t-2xl"></div>
              <div className="relative z-10">
                <div className="w-20 h-20 bg-black border-2 border-green-400 rounded-2xl flex items-center justify-center mb-6 mx-auto group-hover:scale-110 group-hover:-rotate-12 transition-transform duration-300 shadow-neon-green">
                  <svg className="w-10 h-10 text-green-400" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M9.4 16.6L4.8 12l4.6-4.6L8 6l-6 6 6 6 1.4-1.4zm5.2 0L19.2 12l-4.6-4.6L16 6l6 6-6 6-1.4-1.4z" />
                  </svg>
                </div>
                <h3 className="text-2xl font-bold text-green-400 mb-4 font-mono tracking-wider">
                  WEB MATRIX
                </h3>
                <p className="text-gray-300 leading-relaxed mb-6 font-mono text-sm">
                  {'>'} Full-stack architecture<br/>
                  {'>'} Quantum web frameworks<br/>
                  {'>'} Cybersecurity protocols
                </p>
                <div className="flex flex-wrap gap-2">
                  <span className="px-3 py-1 bg-green-400/20 border border-green-400 text-green-400 text-xs font-mono rounded-lg">React</span>
                  <span className="px-3 py-1 bg-cyan-400/20 border border-cyan-400 text-cyan-400 text-xs font-mono rounded-lg">Node.js</span>
                  <span className="px-3 py-1 bg-pink-500/20 border border-pink-500 text-pink-500 text-xs font-mono rounded-lg">Docker</span>
                </div>
              </div>
            </div>

            {/* Language Learning Card */}
            <div className="group relative bg-black/60 backdrop-blur-sm border border-pink-500/50 rounded-2xl p-8 shadow-neon-pink hover:shadow-neon-pink-intense transform hover:-translate-y-4 hover:rotate-1 transition-all duration-500 animate-slideUp delay-1100 overflow-hidden">
              <div className="absolute inset-0 bg-gradient-to-br from-pink-500/10 to-purple-600/10 rounded-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
              <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-transparent via-pink-500 to-transparent animate-slide-right delay-1000 rounded-t-2xl"></div>
              <div className="relative z-10">
                <div className="w-20 h-20 bg-black border-2 border-pink-500 rounded-2xl flex items-center justify-center mb-6 mx-auto group-hover:scale-110 group-hover:rotate-12 transition-transform duration-300 shadow-neon-pink">

                  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" className="w-10 h-10 text-pink-500">
  <path stroke-linecap="round" stroke-linejoin="round" d="m10.5 21 5.25-11.25L21 21m-9-3h7.5M3 5.621a48.474 48.474 0 0 1 6-.371m0 0c1.12 0 2.233.038 3.334.114M9 5.25V3m3.334 2.364C11.176 10.658 7.69 15.08 3 17.502m9.334-12.138c.896.061 1.785.147 2.666.257m-4.589 8.495a18.023 18.023 0 0 1-3.827-5.802" />
</svg>

                </div>
                <h3 className="text-2xl font-bold text-pink-500 mb-4 font-mono tracking-wider">
                  LANG DECODER
                </h3>
                <p className="text-gray-300 leading-relaxed mb-6 font-mono text-sm">
                  {'>'} Multi-language protocols<br/>
                  {'>'} AI translation matrix<br/>
                  {'>'} Neural speech synthesis
                </p>
                <div className="flex flex-wrap gap-2">
                  <span className="px-3 py-1 bg-pink-500/20 border border-pink-500 text-pink-500 text-xs font-mono rounded-lg">English</span>
                  <span className="px-3 py-1 bg-cyan-400/20 border border-cyan-400 text-cyan-400 text-xs font-mono rounded-lg">Japanese</span>
                  <span className="px-3 py-1 bg-green-400/20 border border-green-400 text-green-400 text-xs font-mono rounded-lg">AI Core</span>
                </div>
              </div>
            </div>
          </div>

          {/* Cyberpunk Stats Section */}
          <div className="mt-20 grid grid-cols-2 md:grid-cols-4 gap-8 max-w-4xl mx-auto animate-fadeIn delay-1300">
            <div className="text-center group">
              <div className="w-24 h-24 bg-black/60 border border-cyan-400/50 rounded-2xl flex items-center justify-center mx-auto mb-4 group-hover:scale-110 transition-transform duration-300 shadow-neon-cyan">
                <div className="text-3xl md:text-4xl font-black text-transparent bg-clip-text bg-gradient-to-r from-cyan-400 to-blue-500 font-mono">
                  10+
                </div>
              </div>
              <div className="text-gray-400 font-mono text-sm tracking-wider">NODES ACTIVE</div>
            </div>
            <div className="text-center group">
              <div className="w-24 h-24 bg-black/60 border border-green-400/50 rounded-2xl flex items-center justify-center mx-auto mb-4 group-hover:scale-110 transition-transform duration-300 shadow-neon-green">
                <div className="text-3xl md:text-4xl font-black text-transparent bg-clip-text bg-gradient-to-r from-green-400 to-emerald-500 font-mono">
                ∞
                </div>
              </div>
              <div className="text-gray-400 font-mono text-sm tracking-wider">DATA STREAMS</div>
            </div>
            <div className="text-center group">
              <div className="w-24 h-24 bg-black/60 border border-pink-500/50 rounded-2xl flex items-center justify-center mx-auto mb-4 group-hover:scale-110 transition-transform duration-300 shadow-neon-pink">
                <div className="text-3xl md:text-4xl font-black text-transparent bg-clip-text bg-gradient-to-r from-pink-400 to-purple-500 font-mono">
                ∞
                </div>
              </div>
              <div className="text-gray-400 font-mono text-sm tracking-wider">UPTIME STATUS</div>
            </div>
            <div className="text-center group">
              <div className="w-24 h-24 bg-black/60 border border-purple-500/50 rounded-2xl flex items-center justify-center mx-auto mb-4 group-hover:scale-110 transition-transform duration-300 shadow-neon-purple">
                <div className="text-3xl md:text-4xl font-black text-transparent bg-clip-text bg-gradient-to-r from-purple-400 to-cyan-500 font-mono">
                24
                </div>
              </div>
              <div className="text-gray-400 font-mono text-sm tracking-wider">NEURAL LINK</div>
            </div>
          </div>
        </div>
      </section>

      {/* Learning Resources Section */}
      <section className="py-0 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-r from-cyan-500/5 to-pink-500/5"></div>
        <SliderLearn />
      </section>

      {/* Cyberpunk Call to Action Section */}
      <section className="py-20 px-4 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-r from-black via-gray-900 to-black"></div>
        <div className="absolute inset-0">
          <div className="absolute inset-0 bg-gradient-to-b from-cyan-500/10 via-transparent to-pink-500/10"></div>
        </div>
        
        <div className="relative max-w-4xl mx-auto text-center">
          <div className="mb-8 relative bg-black/60 backdrop-blur-sm border border-gradient-to-r from-cyan-400 to-pink-500 rounded-3xl p-8">
            <h2 className="text-5xl md:text-6xl font-black text-transparent bg-clip-text bg-gradient-to-r from-cyan-400 via-pink-500 to-purple-500 mb-6 font-mono tracking-wider animate-gradient-x">
              [INITIATE_LEARNING]
            </h2>
            <div className="absolute -inset-4 border border-cyan-400/30 rounded-3xl animate-border-pulse"></div>
          </div>
          
          <p className="text-xl text-gray-300 mb-8 max-w-2xl mx-auto font-mono leading-relaxed">
            {'>'} Join the neural network of <span className="text-cyan-400">advanced learners</span><br/>
            {'>'} Upgrade your cognitive capabilities<br/>
            {'>'} Access restricted knowledge databases
            <span className="animate-blink text-green-400">█</span>
          </p>
          
        </div>
      </section>

      <style jsx>{`
        @keyframes fadeIn {
          from { opacity: 0; transform: translateY(30px); }
          to { opacity: 1; transform: translateY(0); }
        }

        @keyframes slideUp {
          from { opacity: 0; transform: translateY(60px) rotateX(30deg); }
          to { opacity: 1; transform: translateY(0) rotateX(0); }
        }

        @keyframes gridPulse {
          0%, 100% { opacity: 0.1; }
          50% { opacity: 0.3; }
        }

        @keyframes float {
          0%, 100% { transform: translateY(0px) rotate(0deg); }
          50% { transform: translateY(-20px) rotate(180deg); }
        }

        @keyframes pulse-neon {
          0%, 100% { opacity: 0.3; transform: scale(1); }
          50% { opacity: 0.6; transform: scale(1.1); }
        }

        @keyframes spin-slow {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }

        @keyframes scanlines {
          0% { transform: translateY(-100%); }
          100% { transform: translateY(100vh); }
        }

        @keyframes glitch {
          0%, 100% { transform: translate(0); }
          20% { transform: translate(-2px, 2px); }
          40% { transform: translate(-2px, -2px); }
          60% { transform: translate(2px, 2px); }
          80% { transform: translate(2px, -2px); }
        }

        @keyframes gradient-x {
          0%, 100% { background-position: 0% 50%; }
          50% { background-position: 100% 50%; }
        }

        @keyframes pulse-fast {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.3; }
        }

        @keyframes blink {
          0%, 50% { opacity: 1; }
          51%, 100% { opacity: 0; }
        }

        @keyframes border-pulse {
          0%, 100% { opacity: 0.3; }
          50% { opacity: 1; }
        }

        @keyframes slide-right {
          0% { transform: translateX(-100%); }
          100% { transform: translateX(400%); }
        }

        @keyframes border-rainbow {
          0% { border-image: linear-gradient(45deg, cyan, transparent, transparent, cyan) 1; }
          25% { border-image: linear-gradient(45deg, transparent, pink, transparent, transparent) 1; }
          50% { border-image: linear-gradient(45deg, transparent, transparent, green, transparent) 1; }
          75% { border-image: linear-gradient(45deg, transparent, transparent, transparent, purple) 1; }
          100% { border-image: linear-gradient(45deg, cyan, transparent, transparent, cyan) 1; }
        }

        .animate-fadeIn { animation: fadeIn 1s ease-out forwards; }
        .animate-slideUp { animation: slideUp 0.8s ease-out forwards; }
        .animate-pulse-neon { animation: pulse-neon 3s ease-in-out infinite; }
        .animate-spin-slow { animation: spin-slow 20s linear infinite; }
        .animate-scanlines { animation: scanlines 2s linear infinite; }
        .animate-glitch { animation: glitch 0.5s ease-in-out; }
        .animate-gradient-x { 
          background-size: 400% 400%;
          animation: gradient-x 3s ease infinite;
        }
        .animate-pulse-fast { animation: pulse-fast 1s ease-in-out infinite; }
        .animate-blink { animation: blink 1s step-start infinite; }
        .animate-border-pulse { animation: border-pulse 2s ease-in-out infinite; }
        .animate-slide-right { animation: slide-right 3s ease-in-out infinite; }
        .animate-border-rainbow { animation: border-rainbow 4s linear infinite; }
        .animate-float { animation: float 6s ease-in-out infinite; }

        .shadow-neon-cyan { box-shadow: 0 0 20px rgba(0,255,255,0.5), 0 0 40px rgba(0,255,255,0.3), 0 0 60px rgba(0,255,255,0.1); }
        .shadow-neon-cyan-intense { box-shadow: 0 0 30px rgba(0,255,255,0.7), 0 0 60px rgba(0,255,255,0.5), 0 0 90px rgba(0,255,255,0.3); }
        .shadow-neon-pink { box-shadow: 0 0 20px rgba(255,0,128,0.5), 0 0 40px rgba(255,0,128,0.3), 0 0 60px rgba(255,0,128,0.1); }
        .shadow-neon-pink-intense { box-shadow: 0 0 30px rgba(255,0,128,0.7), 0 0 60px rgba(255,0,128,0.5), 0 0 90px rgba(255,0,128,0.3); }
        .shadow-neon-green { box-shadow: 0 0 20px rgba(0,255,128,0.5), 0 0 40px rgba(0,255,128,0.3), 0 0 60px rgba(0,255,128,0.1); }
        .shadow-neon-green-intense { box-shadow: 0 0 30px rgba(0,255,128,0.7), 0 0 60px rgba(0,255,128,0.5), 0 0 90px rgba(0,255,128,0.3); }
        .shadow-neon-purple { box-shadow: 0 0 20px rgba(128,0,255,0.5), 0 0 40px rgba(128,0,255,0.3), 0 0 60px rgba(128,0,255,0.1); }
        .shadow-neon-multi { box-shadow: 0 0 20px rgba(0,255,255,0.4), 0 0 40px rgba(255,0,128,0.4), 0 0 60px rgba(0,255,128,0.4); }
        .shadow-neon-multi-intense { box-shadow: 0 0 40px rgba(0,255,255,0.6), 0 0 80px rgba(255,0,128,0.6), 0 0 120px rgba(0,255,128,0.6); }

        .delay-300 { animation-delay: 0.3s; }
        .delay-500 { animation-delay: 0.5s; }
        .delay-700 { animation-delay: 0.7s; }
        .delay-900 { animation-delay: 0.9s; }
        .delay-1000 { animation-delay: 1s; }
        .delay-1100 { animation-delay: 1.1s; }
        .delay-1300 { animation-delay: 1.3s; }
      `}</style>
    </div>
  );
}
